import { useEffect, useState } from 'react';

/**
 * Turns an already-fetched file blob into an object URL so the file can be
 * rendered inline (e.g. in `<object type="application/pdf">`), and revokes that
 * URL again once the blob changes or the component unmounts.
 *
 * An object URL is used rather than the original presigned URL for two reasons:
 * `useFileUrlToTypeMapping` already holds the blob, so rendering costs no second
 * download, and the result does not depend on the Content-Disposition the
 * server happens to send.
 */
export function usePdfObjectUrl(blob?: Promise<Blob>): string | undefined {
  const [objectUrl, setObjectUrl] = useState<string>();

  useEffect(() => {
    let revoked = false;
    let url: string | undefined;

    if (blob) {
      blob
        .then((resolved) => {
          // The effect may already have been cleaned up while the blob was
          // pending; creating the URL then would leak it.
          if (!revoked) {
            url = URL.createObjectURL(resolved);
            setObjectUrl(url);
          }
        })
        .catch(() => {
          // A stale rejection must not clear the URL of a newer blob.
          if (!revoked) {
            setObjectUrl(undefined);
          }
        });
    } else {
      setObjectUrl(undefined);
    }

    return () => {
      revoked = true;
      if (url) {
        URL.revokeObjectURL(url);
      }
      setObjectUrl(undefined);
    };
  }, [blob]);

  return objectUrl;
}
